/**
 * Sovereign Platform Marketplace API Client
 * Wraps @homy/api-client platformClient for Vendors & Properties marketplace moderation.
 */

import { platformClient } from "@homy/api-client";

/**
 * List marketplace vendors with search, category, commune, and verification tier filtering.
 * @param {Object} [params]
 * @param {string} [params.search]
 * @param {string} [params.category]
 * @param {string} [params.commune]
 * @param {string} [params.verificationLevel]
 * @param {number} [params.page=1]
 * @param {number} [params.limit=20]
 * @returns {Promise<{items: Array, total: number, page: number, limit: number}>}
 */
export async function listMarketplaceVendors(params = {}) {
  const response = await platformClient.get("/marketplace/vendors", { params });
  return response.data;
}

/**
 * Update vendor verification tier and moderation audit notes.
 * @param {string} vendorId
 * @param {Object} payload
 * @param {string} payload.verificationLevel - SOURCED, CLAIMED, VERIFIED, PREMIUM (or UNVERIFIED, BASIC, PRO)
 * @param {string} [payload.note]
 * @returns {Promise<{success: boolean, vendor: Object}>}
 */
export async function updateVendorVerification(vendorId, payload) {
  const response = await platformClient.patch(
    `/marketplace/vendors/${vendorId}/verification`,
    payload
  );
  return response.data;
}

/**
 * List properties marketplace listings with status, geographic filters, and syndication health.
 * @param {Object} [params]
 * @param {string} [params.search]
 * @param {string} [params.status]
 * @param {string} [params.country]
 * @param {string} [params.zone]
 * @param {number} [params.page=1]
 * @param {number} [params.limit=20]
 * @returns {Promise<{items: Array, total: number, page: number, limit: number, syndicationHealth: Object}>}
 */
export async function listMarketplaceProperties(params = {}) {
  const response = await platformClient.get("/marketplace/properties", { params });
  return response.data;
}

/**
 * Moderate publication status for a property listing.
 * @param {string} listingId
 * @param {Object} payload
 * @param {string} payload.status - DRAFT, PUBLISHED, ARCHIVED, FLAGGED, ACTIVE
 * @param {string} [payload.reason]
 * @returns {Promise<{success: boolean, listing: Object}>}
 */
export async function updatePropertyListingStatus(listingId, payload) {
  const response = await platformClient.patch(
    `/marketplace/properties/${listingId}/status`,
    payload
  );
  return response.data;
}

export const marketplaceApi = {
  listMarketplaceVendors,
  updateVendorVerification,
  listMarketplaceProperties,
  updatePropertyListingStatus,
};

export default marketplaceApi;
